import { useState } from "react";
import {
  Check,
  CircleCheck,
  Clock,
  Mail,
  Reply,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { Alert } from "@lytra/frontend-common/components/v2/ui/alert";
import { PersonAvatar } from "@/components/PersonAvatar";
import { Badge } from "@lytra/frontend-common/components/v2/ui/badge";
import { Button } from "@lytra/frontend-common/components/v2/ui/button";
import { Throbber } from "@lytra/frontend-common/components/v2/ui/throbber";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@lytra/frontend-common/components/v2/ui/tooltip";
import {
  ForwardMailStatus,
  ForwardRecipientKind,
  ForwardRequestStatus,
} from "@lytra/frontend-common/__generated__/graphql";
import { cn } from "@/lib/utils";
import { deliveryProblemReason } from "./DeliveryProblemNotice";
import type { MentionableAgent } from "./NoteBody";
import type { TicketMessage } from "./noteThreads";
import {
  RECIPIENT_LIMIT,
  failedProblemsFor,
  isViewerRecipient,
  lytraRecipientName,
  unmatchedFailedProblems,
  visibleRecipients,
} from "./forwardRecipients";

/** What the entry reads of a send. */
export type ForwardEntryMessage = Pick<
  TicketMessage,
  "id" | "forwardRecipients" | "forwardMailStatus" | "deliveryProblems"
> &
  Partial<Pick<TicketMessage, "forwardRequestStates">>;

/** The forwarded message as the "Gesendete Nachricht anzeigen" button names it. */
export interface ForwardQuoteSource {
  sender: string;
  /** The time, already formatted for display. */
  at: string;
}

interface FailureAlertProps {
  address: string;
  reason?: string | null;
}

/** The mail to one address did not arrive; the reason is the one the bounce reported (ADR-0057). */
function FailureAlert({ address, reason }: FailureAlertProps) {
  const { t } = useTranslation();
  return (
    <Alert tone="error" announce={false} className="mt-1">
      <p>{t("tickets.forward.entry.deliveryFailed", { address })}</p>
      {reason && <p className="text-ink-muted">{reason}</p>}
    </Alert>
  );
}

const REQUEST_STATUS: Record<
  ForwardRequestStatus,
  { tone: "warning" | "info" | "success" | "neutral"; icon: LucideIcon }
> = {
  [ForwardRequestStatus.Waiting]: { tone: "warning", icon: Clock },
  [ForwardRequestStatus.Confirmed]: { tone: "info", icon: Check },
  [ForwardRequestStatus.Answered]: { tone: "success", icon: Reply },
  [ForwardRequestStatus.Done]: { tone: "neutral", icon: CircleCheck },
};

/** The header's target: the colleagues in Lytra the request went to, as small chips. */
export function ForwardHeaderRecipients({
  recipients,
  viewerId,
  agentsById,
}: Pick<ForwardEntryProps, "viewerId" | "agentsById"> & {
  recipients: readonly ForwardEntryMessage["forwardRecipients"][number][];
}) {
  const { t } = useTranslation();
  const lytra = recipients.filter((r) => r.kind === ForwardRecipientKind.Lytra);
  if (lytra.length === 0) return null;
  const shown = lytra.slice(0, 3);
  const hidden = lytra.length - shown.length;
  return (
    <span
      aria-label={t("tickets.forward.entry.recipients")}
      className="flex min-w-0 shrink-[2] items-center gap-1"
    >
      {shown.map((recipient) => {
        const name = lytraRecipientName(
          recipient,
          agentsById,
          t("tickets.notes.unknownAuthor"),
        );
        const isViewer = isViewerRecipient(recipient, viewerId);
        return (
          <span
            key={String(recipient.userId ?? name)}
            className={cn(
              "flex min-w-0 items-center gap-1 rounded-sm bg-info-subtle py-0.5 pl-0.5 pr-1.5 text-caption font-semibold",
              isViewer ? "text-primary-accent" : "text-info-strong",
            )}
          >
            <PersonAvatar personId={recipient.userId} size="xs" name={name} />
            <span className="min-w-0 truncate">
              {name}
              {isViewer && ` ${t("tickets.forward.entry.you")}`}
            </span>
          </span>
        );
      })}
      {hidden > 0 && (
        <span className="shrink-0 text-caption text-ink-muted">
          {t("tickets.forward.entry.moreRecipients", { count: hidden })}
        </span>
      )}
    </span>
  );
}

interface ForwardEntryProps {
  message: ForwardEntryMessage;
  viewerId?: string;
  agentsById: ReadonlyMap<string, MentionableAgent>;
}

/**
 * What a send to colleagues adds below its text. Colleagues in Lytra are the card's header
 * target; here each one has a status chip (waiting, confirmed, answered, done), which is
 * what makes an internal send a request and not a note. Only the mail leg is listed in
 * full, because only it leaves the company: every address with "Extern" when foreign at
 * send time, and what became of the mail. Delivery is read only from the send's stored
 * state (PENDING, SENT, FAILED), so nothing claims "Gesendet" before Mercury reported it and
 * a send nobody reported on within a day shows FAILED instead of "Wird gesendet …" forever.
 * Everything stored (labels, addresses) is rendered as React text, never HTML (T-03-36).
 */
export function ForwardEntry({
  message,
  viewerId,
  agentsById,
}: ForwardEntryProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const recipients = message.forwardRecipients;
  const mailRecipients = recipients.filter(
    (r) => r.kind !== ForwardRecipientKind.Lytra,
  );
  const lytraRecipients = recipients.filter(
    (r) => r.kind === ForwardRecipientKind.Lytra,
  );
  const stateOf = new Map(
    (message.forwardRequestStates ?? []).map((s) => [
      String(s.userId).toLowerCase(),
      s.status,
    ]),
  );
  const shown = visibleRecipients(mailRecipients, viewerId, expanded);
  const hidden = mailRecipients.length - RECIPIENT_LIMIT;
  const status = message.forwardMailStatus;
  const stray = unmatchedFailedProblems(message.deliveryProblems, recipients);

  return (
    <TooltipProvider>
      <div className="flex min-w-0 flex-col gap-2">
        {lytraRecipients.length > 0 && (
          <ul
            aria-label={t("tickets.forward.entry.requestStatus")}
            className="flex min-w-0 flex-wrap gap-1"
          >
            {lytraRecipients.map((recipient) => {
              const state = stateOf.get(String(recipient.userId).toLowerCase());
              if (!state) return null;
              const name = lytraRecipientName(
                recipient,
                agentsById,
                t("tickets.notes.unknownAuthor"),
              );
              const { tone, icon } = REQUEST_STATUS[state];
              return (
                <li key={String(recipient.userId ?? name)}>
                  <Badge tone={tone} size="sm" icon={icon}>
                    {t(`tickets.forward.entry.status.${state}`, { name })}
                  </Badge>
                </li>
              );
            })}
          </ul>
        )}
        {mailRecipients.length > 0 && (
          <div className="flex min-w-0 flex-col gap-1">
            <p className="text-label text-ink">
              {t("tickets.forward.entry.mailTo")}
            </p>
            <ul
              aria-label={t("tickets.forward.entry.recipients")}
              className="flex min-w-0 flex-col gap-1"
            >
              {shown.map((recipient) => {
                const address = recipient.address ?? recipient.label;
                const storedName = recipient.label.trim();
                const name =
                  storedName &&
                  storedName.toLowerCase() !== address.toLowerCase()
                    ? storedName
                    : address;
                const failures = failedProblemsFor(
                  message.deliveryProblems,
                  address,
                );
                const failed =
                  status === ForwardMailStatus.Failed || failures.length > 0;
                return (
                  <li key={address} className="flex min-w-0 flex-col">
                    {/* Wraps: in a 320-360px panel name, address, "Extern" and the status do not fit one line. */}
                    <div className="flex min-h-6 min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                      <Mail
                        aria-hidden="true"
                        strokeWidth={1.75}
                        className="size-3 shrink-0 text-ink-muted"
                      />
                      <span className="min-w-0 max-w-full truncate text-body text-ink">
                        {name}
                      </span>
                      {name !== address && (
                        <span className="min-w-0 max-w-full truncate text-caption text-ink-muted">
                          {address}
                        </span>
                      )}
                      {recipient.isExternal && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge
                              tone="warning"
                              size="xs"
                              icon={TriangleAlert}
                              tabIndex={0}
                            >
                              {t("tickets.forward.entry.extern")}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t("tickets.forward.warning.foreignPast")}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {!failed && status === ForwardMailStatus.Pending && (
                        <span className="ml-auto flex shrink-0 items-center gap-1 text-caption text-ink-muted">
                          <Throbber size={12} decorative />
                          {t("tickets.forward.entry.sending")}
                        </span>
                      )}
                      {!failed && status === ForwardMailStatus.Sent && (
                        <span className="ml-auto shrink-0 text-caption text-ink-muted">
                          {t("tickets.forward.entry.sent")}
                        </span>
                      )}
                    </div>
                    {failed && (
                      <FailureAlert
                        address={address}
                        reason={
                          failures.length > 0
                            ? deliveryProblemReason(failures[0])
                            : null
                        }
                      />
                    )}
                  </li>
                );
              })}
            </ul>
            {hidden > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="self-start"
                aria-expanded={expanded}
                onClick={() => setExpanded((value) => !value)}
              >
                {expanded
                  ? t("tickets.forward.entry.fewer")
                  : t("tickets.forward.entry.more", { count: hidden })}
              </Button>
            )}
          </div>
        )}
        {stray.map((problem) => (
          <FailureAlert
            key={problem.recipient}
            address={problem.recipient}
            reason={deliveryProblemReason(problem)}
          />
        ))}
      </div>
    </TooltipProvider>
  );
}
